import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useState } from 'react';

import { type Artifact, type FileData, PhotoTab } from '../types.ts/story';
import { extractErrorInfo, extractFileInfo } from '../utility/error';
import { formatUploadToArtifact } from '../utility/general';
import { updateStoryShowcase } from '../utility/story';

import type { ChangeEvent } from 'react';

// Logger (module scope to avoid re-instantiation on each hook call)
const logger = LogManager.getLogger('hook:useFileUpload');

export type FileUploadType =
  | 'storyArtifacts'
  | 'storyArtifactsVideo'
  | 'organizationArtifacts'
  | 'organizationArtifactsVideo'
  | 'organizationLogos'
  | 'all';

export type FileUploadAcceptType = 'image' | 'video' | 'all';

interface UseFileUploadOptions {
  onUploadStart?: () => void;
  onUploadComplete?: (artifacts: Artifact[]) => void;
  onUploadError?: (error: unknown) => void;
  acceptType?: FileUploadAcceptType;
  uploadType?: FileUploadType;
  sizeLimitInMB?: number;
}

const PICTURE_FORMATS_TO_CONVERT = ['image/heic', 'image/heif', 'image/webp'];
const PICTURE_FORMATS = ['image/png', 'image/gif', 'image/jpeg', 'image/jpg'];
const ALL_PICTURE_FORMATS = PICTURE_FORMATS.concat(PICTURE_FORMATS_TO_CONVERT);
const VIDEO_FORMATS = ['video/mp4', 'video/quicktime', 'video/webm'];
const MAX_FILE_SIZE_MB = 750;

const getFileSizeInBytes = (mb: number) => {
  return mb * 1024 * 1024;
};

export const getUploadTypeFromPhotoTab = (tab: PhotoTab) => {
  switch (tab) {
    case PhotoTab.artifact:
      return 'storyArtifacts';
    case PhotoTab.orgArtifact:
      return 'organizationArtifacts';
    case PhotoTab.logo:
      return 'organizationLogos';
    case PhotoTab.storyVideos:
      return 'storyArtifactsVideo';
    case PhotoTab.orgVideos:
      return 'organizationArtifactsVideo';
    default:
      return 'all';
  }
};

export const useFileUpload = (options: UseFileUploadOptions = {}) => {
  const {
    onUploadStart,
    onUploadComplete,
    onUploadError,
    acceptType = 'all',
    uploadType = 'all',
    sizeLimitInMB = MAX_FILE_SIZE_MB,
  } = options;

  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const [isUploading, setIsUploading] = useState(false);

  const getAcceptFormats = useCallback(() => {
    switch (uploadType) {
      case 'storyArtifacts':
      case 'organizationArtifacts':
      case 'organizationLogos':
        return ALL_PICTURE_FORMATS;
      case 'storyArtifactsVideo':
      case 'organizationArtifactsVideo':
        return VIDEO_FORMATS;
      default:
        return acceptType === 'image'
          ? ALL_PICTURE_FORMATS
          : acceptType === 'video'
            ? VIDEO_FORMATS
            : ALL_PICTURE_FORMATS.concat(VIDEO_FORMATS);
    }
  }, [acceptType, uploadType]);

  const uploadFile = useCallback(
    async (file: File, skipSizeCheck?: boolean): Promise<Artifact> => {
      if (!skipSizeCheck && file.size > getFileSizeInBytes(sizeLimitInMB)) {
        videoCreator.showToast({
          state: 'error',
          message: `${sizeLimitInMB}MB file size limit. Please try a smaller file size.`,
        });
        throw new Error('File size limit exceeded');
      }
      const fileName = file.name.split('.')[0];
      const newFileData: FileData & { fileName: string } = {
        type: 'artifact',
        file,
        fileName,
        alt: '',
        title: '',
      };
      const newUpload =
        await datoClientStore.assetRepository?.uploadFile(newFileData);
      return formatUploadToArtifact(newUpload!, {});
    },
    [datoClientStore.assetRepository, sizeLimitInMB],
  );

  const extractGenericInfo = useCallback(() => {
    return {
      uploadType,
      acceptType,
      storyId: videoCreator.story?.id,
      organizationId: videoCreator.organization?.id,
      videoId: videoCreator.currentVideo?.id,
    };
  }, [
    uploadType,
    acceptType,
    videoCreator.story?.id,
    videoCreator.organization?.id,
    videoCreator.currentVideo?.id,
  ]);

  const handleUploadFiles = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = [...(e.target.files || [])];
      if (!files.length) return;

      try {
        setIsUploading(true);
        onUploadStart?.();

        if (files.some(file => file.size > getFileSizeInBytes(sizeLimitInMB))) {
          videoCreator.showToast({
            state: 'error',
            message: `${sizeLimitInMB}MB file size limit. Please try a smaller file size.`,
          });
          throw new Error('File size limit exceeded');
        }
        // Execute all uploads and allow partial success using allSettled
        const settled = await Promise.allSettled(
          files.map(file => uploadFile(file, true)),
        );

        const uploadResults = settled
          .filter(
            (r): r is PromiseFulfilledResult<Artifact> =>
              r.status === 'fulfilled',
          )
          .map(r => r.value)
          .filter(Boolean);

        const failed = settled
          .map((r, index) =>
            r.status === 'rejected' ? { index, reason: r.reason } : null,
          )
          .filter(Boolean) as { index: number; reason: unknown }[];

        if (failed.length) {
          const allFailed = uploadResults.length === 0;
          const status = allFailed ? 'all_failed' : 'partial_failure';
          logger.error('file-upload-failure', {
            event: 'file_upload_batch',
            details: {
              ...extractGenericInfo(),
              status,
              totalFiles: files.length,
              succeededCount: uploadResults.length,
              failedCount: failed.length,
              failedFileDetails: failed.map(f => {
                return {
                  fileInfo: extractFileInfo(files[f.index]),
                  errorInfo: extractErrorInfo(f.reason),
                };
              }),
            },
          });
          onUploadError?.({
            partial: !allFailed,
            failed: failed.map(f => f.reason),
            success: uploadResults,
            status,
          });
          if (allFailed) {
            return; // nothing else to persist
          }
        } else {
          logger.info('file-upload-batch-success', {
            event: 'file_upload_batch',
            details: {
              ...extractGenericInfo(),
              status: 'success',
              totalFiles: files.length,
              succeededFiles: files.length,
              failedFiles: 0,
            },
          });
        }

        const photoUploads =
          uploadResults.filter(u => u?.responsiveImage).filter(Boolean) || [];
        const videoUploads =
          uploadResults.filter(u => !u.responsiveImage).filter(Boolean) || [];

        const storyData = videoCreator.story;

        // Handle story artifacts
        if (
          (photoUploads?.length || videoUploads?.length) &&
          (uploadType === 'all' ||
            uploadType === 'storyArtifactsVideo' ||
            uploadType === 'storyArtifacts')
        ) {
          const existingPhotos = storyData?.storyArtifacts || [];
          const existingVids = storyData?.storyArtifactsVideo || [];

          storyData!.storyArtifactsVideo! = [
            ...existingVids,
            ...videoUploads,
          ] as Artifact[];
          storyData!.storyArtifacts! = [
            ...existingPhotos,
            ...photoUploads,
          ] as Artifact[];

          await videoCreator.updateStory(storyData!);

          if (photoUploads?.length) {
            videoCreator.statusUpdateManager.addStoryArtifactsToWatch(
              storyData!.id,
              photoUploads,
            );
          }
          if (videoUploads?.length) {
            videoCreator.statusUpdateManager.addStoryVideoArtifactsToWatch(
              storyData!.id,
              videoUploads,
            );
          }
        }

        // Handle organization artifacts
        if (
          (photoUploads?.length || videoUploads?.length) &&
          (uploadType === 'organizationArtifactsVideo' ||
            uploadType === 'organizationArtifacts' ||
            uploadType === 'organizationLogos') &&
          storyData?.primaryShowcase
        ) {
          const albumId = storyData.primaryShowcase.id;
          const albumIdx = storyData._allReferencingShowcases.findIndex(
            s => s.id === albumId,
          );
          const album =
            albumIdx > -1
              ? storyData._allReferencingShowcases[albumIdx]
              : storyData.primaryShowcase;

          const existingPhotos = album?.organizationArtifacts || [];
          const existingVids = album?.organizationArtifactsVideo || [];
          const existingLogos = album?.organizationLogos || [];

          album!.organizationArtifactsVideo = [
            ...existingVids,
            ...videoUploads,
          ] as Artifact[];

          if (uploadType === 'organizationLogos') {
            album!.organizationLogos = [
              ...existingLogos,
              ...photoUploads,
            ] as Artifact[];
          } else {
            album!.organizationArtifacts = [
              ...existingPhotos,
              ...photoUploads,
            ] as Artifact[];
          }

          await datoClientStore.albumRepository?.update(album);
          if (videoCreator.organization?.id === album.id)
            videoCreator.organization = album;
          updateStoryShowcase(storyData, album);

          if (photoUploads?.length) {
            videoCreator.statusUpdateManager.addOrganizationArtifactsToWatch(
              album.id,
              photoUploads,
            );
          }
        }

        videoCreator.story = storyData;
        onUploadComplete?.([...photoUploads, ...videoUploads]);

        // Return the first uploaded file for single file uploads
        return uploadResults[0];
      } catch (error) {
        logger.error('file-upload-failure', {
          event: 'file_upload_batch_unexpected_error',
          details: {
            ...extractGenericInfo(),
            errorInfo: extractErrorInfo(error),
            allFilesInfo: files.map(f => extractFileInfo(f)),
          },
        });
        onUploadError?.(error);
      } finally {
        setIsUploading(false);
      }
    },
    [
      uploadFile,
      videoCreator,
      datoClientStore,
      uploadType,
      onUploadStart,
      onUploadComplete,
      onUploadError,
    ],
  );

  const uploadSingleFile = useCallback(
    async (file: File): Promise<Artifact | undefined> => {
      try {
        setIsUploading(true);
        onUploadStart?.();
        const artifact = await uploadFile(file);
        return artifact;
      } catch (error) {
        logger.error('file-upload-failure', {
          event: 'file_upload_single_error',
          details: {
            ...extractGenericInfo(),
            fileInfo: extractFileInfo(file),
            errInfo: extractErrorInfo(error),
          },
        });
        onUploadError?.(error);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadFile, onUploadStart, onUploadError],
  );

  const handleUploadFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = [...(e.target.files || [])];
      if (!files.length) return;
      try {
        setIsUploading(true);
        onUploadStart?.();
        const storyData = videoCreator.story;
        const artifact = await uploadSingleFile(files[0]);
        if (storyData && artifact) {
          if (uploadType === 'storyArtifacts') {
            const storyArtifacts = storyData.storyArtifacts || [];
            storyData.storyArtifacts = [...storyArtifacts, artifact];
            await videoCreator.updateStory(storyData);
            if (artifact.responsiveImage) {
              videoCreator.statusUpdateManager.addStoryArtifactsToWatch(
                storyData.id,
                [artifact],
              );
            } else if (artifact.video) {
              videoCreator.statusUpdateManager.addStoryVideoArtifactsToWatch(
                storyData.id,
                [artifact],
              );
            }
          } else if (
            uploadType === 'organizationArtifacts' &&
            videoCreator.organization
          ) {
            const organization = videoCreator.organization;
            const orgArtifacts = organization.organizationArtifacts || [];
            organization.organizationArtifacts = [...orgArtifacts, artifact];
            await datoClientStore.albumRepository?.update(organization);
            updateStoryShowcase(storyData, organization);
            if (artifact.responsiveImage) {
              videoCreator.statusUpdateManager.addOrganizationArtifactsToWatch(
                organization.id,
                [artifact],
              );
            } else if (artifact.video) {
              videoCreator.statusUpdateManager.addOrganizationVideoArtifactsToWatch(
                organization.id,
                [artifact],
              );
            }
          } else if (
            uploadType === 'organizationLogos' &&
            videoCreator.organization
          ) {
            const organization = videoCreator.organization;
            const orgLogos = organization.organizationLogos || [];
            organization.organizationLogos = [...orgLogos, artifact];
            await datoClientStore.albumRepository?.update(organization);
            updateStoryShowcase(storyData, organization);
          }
        }
        onUploadComplete?.([artifact!]);
      } catch (error) {
        logger.error('file-upload-failure', {
          event: 'file_upload_handle_file_error',
          details: {
            ...extractGenericInfo(),
            errInfo: extractErrorInfo(error),
            fileInfo: files.length ? extractFileInfo(files[0]) : null,
          },
        });
        onUploadError?.(error);
      } finally {
        setIsUploading(false);
      }
    },
    [
      uploadSingleFile,
      videoCreator,
      datoClientStore,
      uploadType,
      onUploadStart,
      onUploadComplete,
      onUploadError,
    ],
  );

  return {
    isUploading,
    handleUploadFiles,
    handleUploadFile,
    acceptFormats: getAcceptFormats(),
  };
};
